import { createContext, useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

const ThemeContext = createContext({
  theme: "light",
  setTheme: () => {},
  toggleTheme: () => {},
});

// Public client-facing pages designed light-only. The global dark-mode CSS
// force-darkens inline white backgrounds, which turns these pages into
// dark-on-dark text, so never apply the dark class on them.
// /delivery/, /track/ and /strategy-call/ are built light-only against fixed
// light palettes.
// /client/vexos-setup/ and /review/ are theme-aware and deliberately absent.
// Keep this list in step with the pre-paint script in public/index.html, which
// applies the same rule before React mounts.
const LIGHT_ONLY_PATHS = [
  /^\/approval\//,
  /^\/track\//,
  /^\/delivery\//,
  /^\/strategy-call\//,
];

export function isLightOnlyPath(pathname = window.location.pathname) {
  return LIGHT_ONLY_PATHS.some((re) => re.test(pathname));
}

/**
 * Keeps the `dark` class honest across ROUTE changes.
 *
 * ThemeProvider's own effect only runs when `theme` changes, so navigating from
 * a dark HQ page into a light-only client page left `dark` applied — which is
 * what used to force those pages dark-on-dark. Every light-only page previously
 * carried its own copy of this effect; this does it once for all of them.
 *
 * Must render INSIDE the Router (ThemeProvider wraps BrowserRouter, so the
 * provider itself cannot call useLocation).
 */
export function LightOnlyThemeSync() {
  const { pathname } = useLocation();
  useEffect(() => {
    const root = document.documentElement;
    const wantsDark = localStorage.getItem("theme") === "dark";
    if (isLightOnlyPath(pathname)) {
      root.classList.remove("dark");
    } else if (wantsDark) {
      root.classList.add("dark");
    }
  }, [pathname]);
  return null;
}

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => {
    // Check localStorage first
    const stored = localStorage.getItem("theme");
    if (stored) return stored;
    // Check system preference
    if (typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches) {
      return "dark";
    }
    return "light";
  });

  useEffect(() => {
    const root = document.documentElement;
    
    if (theme === "dark" && !isLightOnlyPath()) {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
    
    localStorage.setItem("theme", theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === "light" ? "dark" : "light");
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}
